import {
    ConversationalMMAnalyticsEvent as CMMEvent,
    type ConversationalMMAnalyticsEventName as CMMEventName,
    type ConversationalMMTrackEvent,
} from '@fruitful/conversational-mm';
import { AnalyticsUserEvent } from '@fruitful/types';
import { useCallback } from 'react';

import { StorageKeys } from '../../common/constants';
import storage from '../../common/providers/StorageFacade';
import { track } from '../../logger/Logger';

const EVENT_MAP: Record<CMMEventName, AnalyticsUserEvent> = {
    [CMMEvent.ResultsRevealed]: AnalyticsUserEvent.CMM_RESULTS_REVEALED,
    [CMMEvent.ResultsMM]: AnalyticsUserEvent.CMM_RESULTS_MM,
    [CMMEvent.ResultsExplainer]: AnalyticsUserEvent.CMM_RESULTS_EXPLAINER,
    [CMMEvent.ResultsSystem]: AnalyticsUserEvent.CMM_RESULTS_SYSTEM,
    [CMMEvent.ResultsGraph]: AnalyticsUserEvent.CMM_RESULTS_GRAPH,
    [CMMEvent.ResultsSummary]: AnalyticsUserEvent.CMM_RESULTS_SUMMARY,
    [CMMEvent.ResultsSlideViewed]:
        AnalyticsUserEvent.SIGNUP_FUNNEL_CMM_RESULTS_SLIDE,
    [CMMEvent.ResultsPrev]: AnalyticsUserEvent.CMM_RESULTS_PREV,
    [CMMEvent.ResultsNext]: AnalyticsUserEvent.CMM_RESULTS_NEXT,
    [CMMEvent.SlideNextButtonPressed]:
        AnalyticsUserEvent.CMM_RESULTS_SLIDE_NEXT_BUTTON,
    [CMMEvent.SpendGoalsSlider]:
        AnalyticsUserEvent.CMM_RESULTS_SPEND_GOALS_SLIDER,
    [CMMEvent.InvestContribSlider]:
        AnalyticsUserEvent.CMM_RESULTS_INVEST_CONTRIB_SLIDER,
    [CMMEvent.InvestTimeSlider]:
        AnalyticsUserEvent.CMM_RESULTS_INVEST_TIME_SLIDER,
    [CMMEvent.GoalsCardsExpanded]:
        AnalyticsUserEvent.CMM_RESULTS_GOALS_CARDS_EXPANDED,
    [CMMEvent.TooltipPressed]: AnalyticsUserEvent.CMM_RESULTS_TOOLTIP_PRESS,
    [CMMEvent.TooltipsSequenceDone]:
        AnalyticsUserEvent.CMM_RESULTS_TOOLTIPS_DONE,
};

/** Maps the shared package's platform-agnostic analytics events to member-app's real AnalyticsUserEvent/track(). */
export const trackCMMEvent: ConversationalMMTrackEvent = (
    name,
    payload,
    options,
) => {
    track(EVENT_MAP[name], payload, options?.sendImmediately);
};

/** Wires the shared package's guided-tooltip persistence props to member-app's StorageFacade. */
export const useConversationalMMGuidedTooltips = () => {
    const hasSeenGuidedTooltips =
        storage.getBool(
            StorageKeys.HAS_SEEN_CONVERSATIONAL_MM_GUIDED_TOOLTIPS,
        ) ?? false;

    const onGuidedTooltipsSeen = useCallback(() => {
        storage.setBool(
            StorageKeys.HAS_SEEN_CONVERSATIONAL_MM_GUIDED_TOOLTIPS,
            true,
        );
    }, []);

    return { hasSeenGuidedTooltips, onGuidedTooltipsSeen };
};
